import React from "react"
import { useTranslation } from "react-i18next"
import { faClose, faMessage, faPaperPlane, faPhone } from "@fortawesome/free-solid-svg-icons"
import ModalPortal from "../../../../components/Modal/ModalPortal"
import defaultStyle from "../../../../styles/defaultPageStyle.module.css"
import InputField from "../../../../components/InputField/InputField"
import Button, { ButtonIcon } from "../../../../components/Button/Button"
import { TitlePageResponsiveIconSmall } from "../../../../components/Titles/Titles"
import THEME from "../../../../configuration/Theme"
import { SIZE } from "../../../../configuration/Sizes"
import Animate, { AnimationTypes } from "../../../../components/Animate/Animate"
import type { IWhatsAppAgentOverviewDto } from "../../../../Objects/WhatsAppAutomation/WhatsAppAgentOverview"

type Props = {
  isOpen: boolean
  agent: IWhatsAppAgentOverviewDto | null
  toPhone: string
  messageBody: string
  onClose: () => void
  onChangeToPhone: (value: string) => void
  onChangeMessageBody: (value: string) => void
  onSendTest: () => void
}

export const WhatsAppSendTestPopup: React.FC<Props> = ({
  isOpen,
  agent,
  toPhone,
  messageBody,
  onClose,
  onChangeToPhone,
  onChangeMessageBody,
  onSendTest,
}) => {
  const { t } = useTranslation(["pages"])

  if (!isOpen) {
    return null
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              iconColor={THEME.colors.iconBGColor}
              title={t("label_whatsapp_automation_test_title")}
              icon={faPaperPlane}
              subtitleChildren={
                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  size="small"
                  descriptionKey="interactionDescriptions:pages.myRestaurant.whatsAppAutomation.components.whatsAppSendTestPopup.button.onClose"
                />
              }
            />

            <div className="mt-1 p-2 grid gap-3">
              {agent && (
                <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                  <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800">
                    <span className="mt-1 block font-bold">{agent.name}</span>
                  </div>
                </Animate>
              )}

              <InputField
                type="text"
                value={toPhone}
                icon={faPhone}
                inputMode="tel"
                onChange={(event) => onChangeToPhone(event.target.value)}
                placeholder={t("label_whatsapp_automation_to_phone")}
                required
                descriptionKey="interactionDescriptions:pages.myRestaurant.whatsAppAutomation.components.whatsAppSendTestPopup.input.whatsappAutomationToPhone"
              />

              <InputField
                type="text"
                value={messageBody}
                icon={faMessage}
                onChange={(event) => onChangeMessageBody(event.target.value)}
                placeholder={t("label_whatsapp_automation_message_body")}
                required
                maxLength={4000}
                descriptionKey="interactionDescriptions:pages.myRestaurant.whatsAppAutomation.components.whatsAppSendTestPopup.input.whatsappAutomationMessageBody"
              />

              <Button
                bgColor={THEME.colors.buttonBGSaveColor}
                label={t("label_whatsapp_automation_send_test")}
                onClick={() => {
                  onClose()
                  onSendTest()
                }}
                disabled={!agent?.id || !toPhone.trim() || !messageBody.trim()}
                descriptionKey="interactionDescriptions:pages.myRestaurant.whatsAppAutomation.components.whatsAppSendTestPopup.button.whatsappAutomationSendTest"
              />

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                <div className="rounded-lg border-2 border-green-200 bg-slate-50 p-3 text-xs text-slate-600">
                  <strong className="block text-slate-800">
                    {t("label_whatsapp_automation_operational_note_title")}
                  </strong>

                  <p className="mt-1">{t("label_whatsapp_automation_operational_note_description")}</p>
                </div>
              </Animate>
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
